/**
 * Analytics report panels (FEAT-ANALYTICS-001) — one component per GA-style
 * report. Each reads an Event /v2/analytics/* endpoint (via RRS) over the
 * existing multi-source event data. Read-only; no Boards dependency.
 */
import React from 'react';
import { Box, SimpleGrid, Stack, Text, Flex } from '@chakra-ui/react';
import { useReport } from './api';
import {
  StatCard, StatGrid, Panel, TrendChart, BreakdownTable, BreakdownCard, EmptyState, ReportFrame,
  fmtInt, fmtPct, fmtDuration,
} from './components';
import { LeafletMap } from './LeafletMap';
import { countryName } from './geo';
import { fmtMoney } from './txnFormat';
import { CurrencyBreakdown } from './CurrencyBreakdown';

// timeseries [{bucket, ...}] → { categories, series } for TrendChart.
function tsToChart(ts = [], keys = []) {
  const categories = ts.map((r) => String(r.bucket || '').replace(/ 00:00:00$/, '').slice(5));
  const series = keys.map((k) => ({ name: k.label, data: ts.map((r) => Number(r[k.key] || 0)) }));
  return { categories, series };
}

export function OverviewReport({ params }) {
  // Conversions come from the tenant's declared Conversion Events (RRS injects
  // ?conv=). No hardcoded events= list — it over-counted and made Snapshot disagree
  // with the Conversions page (DA-BUG-0451).
  const { data, loading, error } = useReport('overview', params);
  const k = data?.kpis || {};
  const ts = data?.timeseries || [];
  const spark = (key) => ts.map((r) => Number(r[key] || 0));
  const countries = (data?.byCountry || []).map((r) => ({ label: countryName(r.label), value: r.value }));
  return (
    <ReportFrame loading={loading} error={error}>
      {data && (
        <Stack spacing={4}>
          {/* Full-bleed grey location map (GA overview style) */}
          <Box>
            <Text fontSize="xs" fontWeight="700" letterSpacing="0.06em" color="gray.500" textTransform="uppercase" mb={2}>Users by location</Text>
            <Box position="relative" overflow="hidden" mx={{ base: -3, md: -6 }} borderTop="1px solid" borderBottom="1px solid" borderColor="gray.200">
              <LeafletMap byCountry={data.byCountry} byCity={data.byCity} height={440} fitData />
            </Box>
          </Box>

          {/* KPI boxes with GA-style sparklines */}
          <StatGrid>
            <StatCard label="Users" value={fmtInt(k.users)} delta={k.deltas?.users} spark={spark('users')} />
            <StatCard label="Sessions" value={fmtInt(k.sessions)} delta={k.deltas?.sessions} spark={spark('sessions')} />
            <StatCard label="Pageviews" value={fmtInt(k.pageviews)} delta={k.deltas?.pageviews} spark={spark('pageviews')} />
            <StatCard label="Conversions" value={fmtInt(k.conversions)} delta={k.deltas?.conversions} />
            <StatCard label="New users" value={fmtInt(k.new_users)} />
            <StatCard label="Returning users" value={fmtInt(k.returning_users)} help="First seen before this period" />
            <StatCard label="Conv. rate" value={fmtPct(k.conversion_rate)} />
            <StatCard label="Bounce rate" value={fmtPct(k.bounce_rate * 100)} />
            <StatCard label="Avg. session" value={fmtDuration(k.avg_session_seconds)} help={`${k.pages_per_session} pages/session`} />
          </StatGrid>

          {/* Trend + breakdown cards */}
          <Panel title="Trend">
            <TrendChart {...tsToChart(data.timeseries, [
              { key: 'sessions', label: 'Sessions' },
              { key: 'users', label: 'Users' },
              { key: 'pageviews', label: 'Pageviews' },
            ])} />
          </Panel>
          <SimpleGrid columns={{ base: 1, lg: 3 }} spacing={4}>
            <BreakdownCard title="Users by channel" rows={data.byChannel} labelHeader="Channel" />
            <BreakdownCard title="Users by country" rows={countries} labelHeader="Country" />
            <BreakdownCard title="Top pages" rows={data.topPages} labelHeader="Page / screen" />
          </SimpleGrid>
        </Stack>
      )}
    </ReportFrame>
  );
}

export function AudienceReport({ params }) {
  const { data, loading, error } = useReport('audience', params);
  return (
    <ReportFrame loading={loading} error={error}>
      {data && (
        <Stack spacing={4}>
          <StatGrid columns={{ base: 2, md: 2 }}>
            <StatCard label="New users" value={fmtInt(data.newVsReturning?.new_users)} />
            <StatCard label="Returning users" value={fmtInt(data.newVsReturning?.returning_users)} />
          </StatGrid>
          <Panel title="Users by location">
            <Box borderRadius="lg" overflow="hidden">
              <LeafletMap byCountry={data.byCountry} byCity={data.byCity} height={380} fitData />
            </Box>
          </Panel>
          <Panel title="Users & sessions over time">
            <TrendChart {...tsToChart(data.timeseries, [
              { key: 'users', label: 'Users' }, { key: 'sessions', label: 'Sessions' },
            ])} />
          </Panel>
          <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4}>
            {/* Platform / data stream — iOS vs Android (vs Web) shown by default (FEAT-ANALYTICS-PROPERTY). */}
            <BreakdownCard title="Platform" rows={data.byStream} labelHeader="Platform" />
            <BreakdownCard title="Device" rows={data.byDevice} labelHeader="Device" />
            <BreakdownCard title="Browser" rows={data.byBrowser} labelHeader="Browser" />
            <BreakdownCard title="Operating system" rows={data.byOS} labelHeader="OS" />
            <BreakdownCard title="Country" rows={(data.byCountry || []).map((r) => ({ label: countryName(r.label), value: r.value }))} labelHeader="Country" />
            <BreakdownCard title="Region" rows={data.byRegion} labelHeader="Region" />
            <BreakdownCard title="City" rows={data.byCity} labelHeader="City" />
          </SimpleGrid>
        </Stack>
      )}
    </ReportFrame>
  );
}

export function AcquisitionReport({ params }) {
  const { data, loading, error } = useReport('acquisition', params);
  return (
    <ReportFrame loading={loading} error={error}>
      {data && (
        <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={4}>
          <Panel title="Source (utm_source)"><BreakdownTable rows={data.bySource} labelHeader="Source" valueHeader="Sessions" /></Panel>
          <Panel title="Medium (utm_medium)"><BreakdownTable rows={data.byMedium} labelHeader="Medium" valueHeader="Sessions" /></Panel>
          <Panel title="Campaign (utm_campaign)"><BreakdownTable rows={data.byCampaign} labelHeader="Campaign" valueHeader="Sessions" /></Panel>
          <Panel title="Referrer"><BreakdownTable rows={data.byReferrer} labelHeader="Referrer" valueHeader="Sessions" /></Panel>
          <Panel title="Channel"><BreakdownTable rows={data.byChannel} labelHeader="Channel" valueHeader="Sessions" /></Panel>
        </SimpleGrid>
      )}
    </ReportFrame>
  );
}

export function BehaviorReport({ params }) {
  const { data, loading, error } = useReport('behavior', params);
  return (
    <ReportFrame loading={loading} error={error}>
      {data && (
        <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={4}>
          <Panel title="Top pages"><BreakdownTable rows={data.topPages} labelHeader="Page" valueHeader="Views" /></Panel>
          <Panel title="Top events"><BreakdownTable rows={data.topEvents} labelHeader="Event" valueHeader="Count" /></Panel>
          <Panel title="Landing pages"><BreakdownTable rows={data.landingPages} labelHeader="Page" valueHeader="Sessions" /></Panel>
          <Panel title="Exit pages"><BreakdownTable rows={data.exitPages} labelHeader="Page" valueHeader="Sessions" /></Panel>
        </SimpleGrid>
      )}
    </ReportFrame>
  );
}

export function ProductsReport({ params }) {
  const { data, loading, error } = useReport('products', params);
  const f = (data && data.funnel) || {};
  return (
    <ReportFrame loading={loading} error={error}>
      {data && (
        <Stack spacing={4}>
          <Panel title="Product funnel">
            <StatGrid>
              <StatCard label="Viewed" value={fmtInt(f.viewed)} />
              <StatCard label="Added to cart" value={fmtInt(f.added_to_cart)} help={f.viewed ? fmtPct((f.added_to_cart / f.viewed) * 100) : undefined} />
              <StatCard label="Checkout" value={fmtInt(f.checkout)} help={f.viewed ? fmtPct((f.checkout / f.viewed) * 100) : undefined} />
              <StatCard label="Purchased" value={fmtInt(f.purchased)} help={f.viewed ? fmtPct((f.purchased / f.viewed) * 100) : undefined} />
            </StatGrid>
          </Panel>
          <Panel title="Top viewed products"><BreakdownTable rows={data.topProducts} labelHeader="Product" valueHeader="Views" limit={20} /></Panel>
        </Stack>
      )}
    </ReportFrame>
  );
}

export function ConversionsReport({ params }) {
  const { data, loading, error } = useReport('conversions', params);
  // Conversion value is money — format it as money, and show it PER CURRENCY when a
  // tenant converts in more than one currency (never blended; DA-BUG-0455).
  const byCur = data?.value_by_currency || [];
  const multi = byCur.length > 1;
  const cur = byCur.length === 1 ? byCur[0].currency : '';
  return (
    <ReportFrame loading={loading} error={error}>
      {data && (
        <Stack spacing={4}>
          <StatGrid columns={{ base: 1, md: 3 }}>
            <StatCard label="Conversions" value={fmtInt(data.conversions)} />
            <StatCard label="Conv. rate" value={fmtPct(data.conversion_rate)} />
            <StatCard label="Value" value={multi ? 'By currency ↓' : fmtMoney(data.value, cur)} />
          </StatGrid>
          {multi && (
            <CurrencyBreakdown title="Conversion value by currency" rows={byCur} cols={[{ key: 'value', label: 'Value', money: true }]} />
          )}
          <Panel title="Conversions over time">
            <TrendChart {...tsToChart(data.timeseries, [{ key: 'conversions', label: 'Conversions' }])} />
          </Panel>
          <SimpleGrid columns={{ base: 1, lg: 3 }} spacing={4}>
            <Panel title="By channel"><BreakdownTable rows={data.byChannel} labelHeader="Channel" /></Panel>
            <Panel title="By source"><BreakdownTable rows={data.bySource} labelHeader="Source" /></Panel>
            <Panel title="By page"><BreakdownTable rows={data.byPage} labelHeader="Page" /></Panel>
          </SimpleGrid>
        </Stack>
      )}
    </ReportFrame>
  );
}

export function RealtimeReport({ params } = {}) {
  // Realtime ignores the date window (always last 30 min) and auto-refreshes.
  const [tick, setTick] = React.useState(0);
  React.useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 20000); // poll every 20s like GA
    return () => clearInterval(id);
  }, []);
  const channel = params?.channel;
  const { data, loading, error } = useReport('realtime', { ...(channel ? { channel } : {}), _t: tick });

  const k = data?.kpis || {};
  const countries = (data?.byCountry || []).map((r) => ({ label: countryName(r.label), value: r.value }));
  const cities = (data?.byCity || []).map((r) => ({ label: r.country ? `${r.label}, ${countryName(r.country)}` : r.label, value: r.value }));

  const hasLive = (data?.byCountry || []).length > 0 || (data?.byCity || []).length > 0;

  return (
    <ReportFrame loading={loading && !data} error={error}>
      {data && (
        <Stack spacing={4}>
          <Flex align="center" gap={2}>
            <Box w="8px" h="8px" borderRadius="full" bg="green.400" />
            <Text fontSize="sm" color="gray.500">Live · last 30 minutes · auto-refresh 20s</Text>
          </Flex>

          {/* Hero: full-bleed grey basemap (Leaflet/CARTO) with a live-counter card
              overlaid on the left, matching GA's realtime overview. Negative margins
              escape the content pane's padding so the map spans edge-to-edge. */}
          <Box position="relative" overflow="hidden" mx={{ base: -3, md: -6 }} mt={-1} borderTop="1px solid" borderBottom="1px solid" borderColor="gray.200">
            <LeafletMap byCountry={data.byCountry} byCity={data.byCity} height={520} pulse fitData />
            <Box position="absolute" top="12px" left="12px" bg="white" borderRadius="lg" boxShadow="0 4px 16px rgba(16,24,40,.18)" p={4} w={{ base: '200px', md: '300px' }} zIndex={1000}>
              <SimpleGrid columns={2} spacing={3}>
                <Box>
                  <Text fontSize="10px" fontWeight="700" letterSpacing="0.05em" color="gray.500" textTransform="uppercase">Active users · 30 min</Text>
                  <Text fontSize="30px" fontWeight="800" color="#111827" fontFamily="mono" lineHeight="1.1">{fmtInt(k.active_users)}</Text>
                </Box>
                <Box>
                  <Text fontSize="10px" fontWeight="700" letterSpacing="0.05em" color="gray.500" textTransform="uppercase">Active · 5 min</Text>
                  <Text fontSize="30px" fontWeight="800" color="#111827" fontFamily="mono" lineHeight="1.1">{fmtInt(k.active_users_5min)}</Text>
                </Box>
              </SimpleGrid>
              <Text fontSize="10px" fontWeight="700" letterSpacing="0.05em" color="gray.500" textTransform="uppercase" mt={3} mb={1}>Active users per minute</Text>
              <TrendChart height={70}
                categories={(data.byMinute || []).map((r) => String(r.bucket || '').slice(11, 16))}
                series={[{ name: 'Users', data: (data.byMinute || []).map((r) => Number(r.users || 0)) }]} />
            </Box>
            {!hasLive && (
              <Box position="absolute" bottom="12px" left="12px" bg="whiteAlpha.900" borderRadius="md" px={3} py={1} zIndex={1000}>
                <Text fontSize="xs" color="gray.500">No active users in the last 30 minutes</Text>
              </Box>
            )}
          </Box>

          {/* Breakdown cards — GA's realtime grid */}
          <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4}>
            <BreakdownCard title="Active users by country" rows={countries} labelHeader="Country" />
            <BreakdownCard title="Active users by city" rows={cities} labelHeader="City" />
            <BreakdownCard title="Active users by channel" rows={data.byChannel} labelHeader="Channel" />
            <BreakdownCard title="Active users by page / screen" rows={data.topPages} labelHeader="Page / screen" />
            <BreakdownCard title="Event count by event" rows={data.topEvents} labelHeader="Event" valueHeader="Count" />
          </SimpleGrid>
        </Stack>
      )}
    </ReportFrame>
  );
}
